import { Component, ViewEncapsulation, OnInit, OnDestroy, ElementRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';

import { Addresses } from 'src/providers/customer/addresses';
import { Card } from 'src/providers/card/card';
import { Client } from 'src/providers/client/client';
import { Me } from 'src/providers/me/me';
import { Webhooks } from 'src/providers/webhooks/webhooks';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { Events } from 'src/providers/events/events';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';
import { getDisplayLanguageName } from 'src/shared/constants/display-languages';

import { AddressModel } from 'src/models/address.model';
import { ClientModel, ClientSettingsModel } from 'src/models/client.model';
import { ScheduleAssuranceModel } from 'src/models/schedule-assurance.model'
import { CurrentAddress } from 'src/providers/addresses/current-address';
import { Genome } from 'src/providers/genome/genome';
import { OnboardingProvider } from 'src/providers/onboarding/onboarding.provider';
import { Subscription } from 'rxjs';
import { BREAK_LARGE_RESOLUTION } from 'android/app/src/constants/app.contants';
import { WindowService } from 'src/shared/providers/window.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TranslationService } from 'src/tidy-ui-components/providers/translation.service';
import { BillingHistoryPage } from '../billing-history/billing-history';
import { ConciergePage } from '../concierge/concierge';
import { UsersPage } from '../users/users';
import { TeamsPage } from '../team/teams/teams';
import { PaymentMethodsPage } from './payment-methods/payment-methods';
import { LoadCreditPage } from './payment-methods/load-credit/load-credit';
import { PaymentSettingsPage } from './payment-settings/payment-settings';
import { UseGiftCardPage } from './use-gift-card/use-gift-card';
import { MyPricesPage } from './my-prices/my-prices';
import { ContactInfoPage } from './contact-info/contact-info';
import { SubscriptionsPage } from '../subscription/subscription';
import { AccountTypePage } from './account-type/account-type';
import { BackupTimesPage } from './backup-times/backup-times';
import { AccountNotificationSettingsPage } from './account-notification-settings/account-notification-settings';
import { EditLanguagePage } from './edit-language/edit-language';
import { ServiceRenamePage } from './service-rename/service-rename';
import { DevelopersPage } from './developers/developers';
import { CancellationOptionsPage } from './cancellation-options/cancellation-options';
import { CustomFieldsPage } from './custom-fields/custom-fields';
import { GoLivePage } from '../go-live/go-live.page';
import { OnboardingComponent } from 'src/shared/components/onboarding/onboarding';
import { BuyBundlePage } from './buy-bundle/buy-bundle';
import { BulkUploadPage } from './bulk-upload/bulk-upload';
import { ThemeService } from 'src/shared/services/theme.service';
import { DemoBlockService } from 'src/shared/services/demo-block.service';
import { DemoAccountService } from 'src/shared/services/demo-account.service';
import { AccountManagerService } from 'src/shared/services/account-manager.service';
import { RentalModesService } from 'src/shared/services/rental-modes.service';
import { OnboardingStatusComponent } from 'src/shared/components/onboarding-status/onboarding-status.component';
import { GetStartedFlowComponent } from 'src/shared/components/get-started-flow/get-started-flow.component';
@Component({
    templateUrl: 'more.html',
    styleUrls: ['more.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})

export class MorePage implements OnInit, OnDestroy {
  accountType: string;
  addressResponse: AddressModel[];
  addressResponseFiltered: AddressModel[];
  clientInfo: ClientModel;
  contactName: string;
  hasBackupTimes: boolean;
  hasPrivatePro: boolean;
  hasThreePlusAddresses: boolean;
  isDesktopRes: boolean;
  isEmployeeAccount: boolean;
  isRentalClient: boolean;
  language: string;
  loaded: boolean;
  loadedAddressesDetails = false;
  loginEmail: string;
  notificationSettings: any;
  scheduleAssurance: ScheduleAssuranceModel;
  searchForm: UntypedFormGroup;
  searchTerm = '';
  settings: ClientSettingsModel;
  userRole: string | null;
  webhooks: any;
  panelClosedSubscription: Subscription;
  private resizeHandler: () => void;
  onboardingPercentage: number = null;
  showOnboarding = false;
  // Live onboarding indicator (mirrors the desktop floating pill) — rendered at the top
  // of the More page on mobile so the onboarding journey is reachable without the desktop pill.
  showLivePill = false;
  livePropertiesCount = 0;
  livePropertiesTotal = 0;
  actionItemsCount = 0;
  callScheduled = false;
  setupCallCompleted = false;
  // The customer's assigned account manager (from /me), or TIDY's default until one is set.
  managerName = AccountManagerService.DEFAULT.name;
  managerPhoto = AccountManagerService.DEFAULT.photoUrl;
  // Gates the long-term-only rows below (Leasing, eSignature, Deposits, Rent Collection).
  longTermRentals = true;
  AccountNotificationSettingsPage = AccountNotificationSettingsPage;
  AccountTypePage = AccountTypePage;
  BackupTimesPage = BackupTimesPage;
  CancellationOptionsPage = CancellationOptionsPage;
  ContactInfoPage = ContactInfoPage;
  CustomFieldsPage = CustomFieldsPage;
  DevelopersPage = DevelopersPage;
  EditLanguagePage = EditLanguagePage;
  LoadCreditPage = LoadCreditPage;
  MyPricesPage = MyPricesPage;
  PaymentMethodsPage = PaymentMethodsPage;
  PaymentSettingsPage = PaymentSettingsPage;
  ServiceRenamePage = ServiceRenamePage;
  SubscriptionsPage = SubscriptionsPage;
  TeamsPage = TeamsPage;
  UseGiftCardPage = UseGiftCardPage;
  UsersPage = UsersPage;
  BulkUploadPage = BulkUploadPage;
  BuyBundlePage = BuyBundlePage;

  constructor(
    private addresses: Addresses,
    private cardService: Card,
    private client: Client,
    private currentAddress: CurrentAddress,
    private events: Events,
    private fb: UntypedFormBuilder,
    private genome: Genome,
    private me: Me,
    private navCtrl: CustomNavController,
    public onboardingProvider: OnboardingProvider,
    private storage: TidyStorage,
    private util: Util,
    private webhooksService: Webhooks,
    public windowService: WindowService,
    private translationService: TranslationService,
    private rightSidePanelService: RightSidePanelService,
    public themeService: ThemeService,
    private demoBlock: DemoBlockService,
    private demoAccount: DemoAccountService,
    private accountManagerService: AccountManagerService,
    private rentalModesService: RentalModesService,
    private elementRef: ElementRef
  ) {
    // Show/hide the long-term rental sections as Account Settings toggles.
    // modes$ never completes, so tie it to this page's lifetime (constructor is an
    // injection context, so takeUntilDestroyed needs no explicit DestroyRef).
    this.rentalModesService.modes$
      .pipe(takeUntilDestroyed())
      .subscribe((modes) => {
        this.longTermRentals = modes.longTerm;
      });
    this.searchForm = this.fb.group({
      search: ['']
    });
    this.searchForm.valueChanges.pipe(
      debounceTime(300)
    ).subscribe((val) => this.updateSearch(val));
  }

  ngOnInit() {
    this.getPageData();
    // The More page is a hub of account/settings sub-pages that mustn't be touched in
    // the shared demo account, so block every action on it. A single capture-phase
    // listener intercepts any click before its (click) handler runs and shows the
    // "create an account" prompt instead (search typing is left alone). No-ops outside
    // the demo session.
    this.elementRef.nativeElement.addEventListener('click', this.demoClickInterceptor, true);
  }

  // Returning here from the full-page onboarding route (mobile back button) doesn't re-run
  // ngOnInit, so refresh the live pill on every entry to reflect any call scheduled or
  // listing connected while away. Guarded by `loaded` to avoid a redundant fetch during
  // the initial load (getPageData already refreshes it then).
  ionViewWillEnter(): void {
    if (this.loaded) {
      this.refreshLivePill();
    }
  }

  private demoClickInterceptor = (event: Event): void => {
    if (!this.demoBlock.isDemo) {
      return;
    }
    const target = event.target as HTMLElement;
    // Let the user keep navigating away (bottom nav), typing in the search field, and
    // logging out (.demo-allow-action) — everything else on the More page is gated.
    if (target?.closest('tidy-bottom-nav, input, textarea, ion-searchbar, tidy-search-input, tidy-input, .demo-allow-action')) {
      return;
    }
    event.stopPropagation();
    event.preventDefault();
    this.demoBlock.block();
  };

  async getPageData() {
    try {
      const shouldReloadLocation = localStorage.getItem('shouldReloadLocation') === 'true';
      if (shouldReloadLocation) {
        localStorage.setItem('shouldReloadLocation', 'false');
        return location.reload();
      }
      const aboutThisPageData = localStorage.getItem('aboutThisPageData');
      if (aboutThisPageData) {
        this.showOnboarding = true;
      }
      this.isDesktopRes = window.innerWidth > BREAK_LARGE_RESOLUTION;
      this.loaded = false;
      const didUpdateLanguage = localStorage.getItem('didUpdateLanguage');
      if (didUpdateLanguage === 'false') {
        const currentLanguage = localStorage.getItem('currentLanguage');
        localStorage.setItem('language', currentLanguage);
      }
      localStorage.removeItem('currentLanguage');
      localStorage.removeItem('didUpdateLanguage');
      this.language = this.getLanguage();
      this.addressResponse = await this.client.getAddresses();
      if (this.addressResponse.length > 2) {
        this.hasThreePlusAddresses = true;
      }
      this.userRole = localStorage.getItem('user_role');
      this.addressResponseFiltered = this.addressResponse.filter((address, i) => i < 2 );
      this.addressResponseFiltered = await Promise.all(this.addressResponseFiltered.map(async (address) => {
        const addressDetails = await this.genome.getAddress(address.id);
        return {
          ...address,
          ...addressDetails
        }
      }))
      this.loadedAddressesDetails = true;
      this.clientInfo = await this.client.getClientInfo();
      this.settings = await this.client.getClientSettings();
      this.contactName = this.getContactName();
      this.loginEmail = this.clientInfo?.current_user?.email;
      if (this.userRole !== 'member') {
        const withUrl = false;
        this.webhooks = await this.webhooksService.getWebhooks();
        this.notificationSettings = await this.webhooksService.buildNotificationSettingsArray(this.webhooks, withUrl);
        this.scheduleAssurance = await this.client.getScheduleAssurance();
        this.checkForBackupTimes();
      }
      this.hasPrivatePro = localStorage.getItem('hasPrivatePro') === 'true';
      this.isRentalClient = localStorage.getItem('isRentalClient') === 'true';
      this.accountType = this.getAccountType();
      if (this.isRentalClient) {
        this.calculateOnboardingPercentage();
      }
      this.refreshLivePill();
      this.isEmployeeAccount = this.clientInfo?.customer_account?.account_type === 'employer';
      this.loaded = true;
      this.resizeHandler = () => {
        this.isDesktopRes = window.innerWidth > BREAK_LARGE_RESOLUTION;
      };
      window.addEventListener('resize', this.resizeHandler);
      localStorage.setItem('shouldListenToLanguageChange', 'true');
    } catch (_err) {
      // Error handled silently - page continues with default values
    }
  }

  ngOnDestroy() {
    localStorage.setItem('shouldListenToLanguageChange', 'false');
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
    this.elementRef.nativeElement.removeEventListener('click', this.demoClickInterceptor, true);
  }

  getLanguage() {
    return getDisplayLanguageName(localStorage.getItem('language') || 'en');
  }

  getAccountType() {
    const types = {
      regular: 'Residential',
      realtor: 'Realtor',
      rental: 'Rental',
      company: 'Office',
    };
    return types[this.clientInfo.customer_account.account_type];
  }

  // The Contact Info card edits the account record, so every line on it reads from
  // settings.profile — including the name, which otherwise describes the signed-in
  // member and contradicts the account email beneath it.
  getContactName() {
    return [this.settings?.profile?.first_name, this.settings?.profile?.last_name]
      .filter(Boolean)
      .join(' ');
  }

  logout() {
    this.events.publish('logout');
    localStorage.clear();
  }

  checkForBackupTimes() {
    this.scheduleAssurance.customerAddress.times.forEach((time) => {
      if (time.start_time !== null) {
        return this.hasBackupTimes = true;
      }
    })
  }

  updateSearch({ search }) {
    this.searchTerm = search?.toLowerCase() || '';
  }

  matchesSearch(keywords: string[]): boolean {
    if (!this.searchTerm) {
      return true;
    }
    return keywords.some(keyword => {
      const lowerKeyword = keyword.toLowerCase();
      return lowerKeyword.includes(this.searchTerm) || this.searchTerm.includes(lowerKeyword);
    });
  }

  async openRightSidePanel(url: string, component: any, params: any): Promise<void> {
    if (url === 'backup-times' || url === 'billing-history' || url === 'my-prices') {
      const hasProperties = await this.onboardingProvider.checkIfHasProperties();
      if (!hasProperties) {
        const page = {
          'backup-times': 'your backup times',
          'billing-history': 'your billing history',
          'my-prices': 'your prices',
        }
        return this.util.showError(`Please add a property before viewing ${page[url]}.`);
      }
    }
    localStorage.setItem('integrationsBackPage', 'more')
    localStorage.setItem('automaticBookingBackPage', 'more');
    params = {
      ...params,
      successRoute: 'more',
      settings: this.settings,
      webhooks: this.webhooks,
      backPage: 'more',
      setting: this.util.deepClone(this.settings)
    };
    await this.storage.save('dialog-params', params);
    if (this.windowService.isDesktopRes) {
      await this.storage.save('dialog-right-side-open', true);
      this.rightSidePanelService.openRightSidePanel(component);
      this.panelClosedSubscription = this.rightSidePanelService.panelClosed().subscribe(async () => {
        const didUpdateLanguage = localStorage.getItem('didUpdateLanguage');
        if (didUpdateLanguage === 'false') {
          const currentLanguage = localStorage.getItem('currentLanguage');
          localStorage.setItem('language', currentLanguage);
          this.translationService.notifyLanguageChange();
        }
        localStorage.removeItem('currentLanguage');
        localStorage.removeItem('didUpdateLanguage');
        const didMakeMoreChange = localStorage.getItem('didMakeMoreChange') === 'true';
        if (didMakeMoreChange) {
          localStorage.setItem('didMakeMoreChange', 'false');
          this.getPageData();
        }
      });
    } else {
      await this.storage.save('dialog-right-side-open', false);
      this.navCtrl.navigateForward(url)
    }
  }

  goToAddresses() {
    this.navCtrl.navigateForward('properties');
  }

  goToAddAddress() {
    this.navCtrl.navigateForward('add-property');
  }

  goToPastJobs() {
    this.navCtrl.navigateForward('past-jobs');
  }

  async goToBillingHistory() {
    localStorage.setItem('billingHistoryBackPage', 'more');
    this.openRightSidePanel('billing-history', BillingHistoryPage, {});
  }

  goToApiDocumentation() {
    this.util.openUrl('https://tidy.stoplight.io/docs/tidy-api-spec/docs/1.%20Introduction.md');
  }

  goToAuditLogs() {
    this.navCtrl.navigateForward('audit-logs');
  }

  goToToDosPage() {
    localStorage.setItem('cameFromPage', 'more');
    this.navCtrl.navigateForward('to-dos');
  }

  goToMaintenancePage() {
    localStorage.setItem('cameFromPage', 'more');
    this.navCtrl.navigateForward('maintenance');
  }

  goToSchedulePage() {
    localStorage.setItem('cameFromPage', 'more');
    this.navCtrl.navigateForward('schedule');
  }

  goToMessagesPage() {
    localStorage.setItem('cameFromPage', 'more');
    this.navCtrl.navigateForward('messages');
  }

  goToEditAddress(address) {
    this.currentAddress.addressId = address.id;
    const params = {};
    const addressHasMoreDetail = address.home_access;
    if (addressHasMoreDetail) {
      params['address'] = address;
    }
    this.navCtrl.navigateForward(`properties?propertyId=${address.id}`);
  }

  goToConcierge() {
    this.openRightSidePanel('concierge', ConciergePage, {});
  }

  goToDocumentation() {
    const url = 'https://www.tidy.com/docs/pm/getting-started';
    this.util.openUrl(url);
  }

  goToCustomFields() {
    this.openRightSidePanel('more/custom-fields', CustomFieldsPage, {});
  }

  goToGuestReservations() {
    this.navCtrl.navigateForward('more/reservations');
  }

  goToIntegrations() {
    localStorage.setItem('integrationsBackPage', 'more');
    this.navCtrl.navigateForward('integration/all');
  }

  goToReports() {
    this.navCtrl.navigateForward('reports');
  }

  goToRentCollection() {
    this.navCtrl.navigateForward('rent-collection');
  }

  goToFinanceTab(tab: string) {
    this.navCtrl.navigateForward(`finance?tab=${tab}`);
  }

  goToBillsPage() {
    localStorage.setItem('cameFromPage', 'more');
    this.navCtrl.navigateForward('bills');
  }

  goToInventoryPage() {
    localStorage.setItem('cameFromPage', 'more');
    this.navCtrl.navigateForward('restocking');
  }

  goToESign() {
    this.navCtrl.navigateForward('e-sign');
  }

  goToAssetsPage() {
    localStorage.setItem('cameFromPage', 'more');
    this.navCtrl.navigateForward('assets');
  }

  goToUpsellsPage() {
    localStorage.setItem('cameFromPage', 'more');
    this.navCtrl.navigateForward('upsells');
  }

  goToListings() {
    this.navCtrl.navigateForward('listings');
  }

  goToMarketingTab(tab: string) {
    this.navCtrl.navigateForward(`revenue?tab=${tab}`);
  }

  // Screenings is a section of Leasing, so it needs the sub-tab param too.
  goToLeasingScreenings() {
    this.navCtrl.navigateForward('revenue?tab=leasing&leasingTab=screenings');
  }

  // Section-card destinations (rental mobile More page — Revenue / Operations
  // / Finance cards mirror the desktop nav).
  goToMarketingSummary() {
    this.navCtrl.navigateForward('marketing-summary');
  }

  goToOptimizer() {
    this.navCtrl.navigateForward('optimizer');
  }

  goToDirectBooking() {
    this.navCtrl.navigateForward('direct-booking');
  }

  goToInquiries() {
    this.navCtrl.navigateForward('inquiries');
  }

  goToUpgrades() {
    this.navCtrl.navigateForward('upgrades');
  }

  goToAccess() {
    this.navCtrl.navigateForward('access');
  }

  goToCompliancePage() {
    this.navCtrl.navigateForward('compliance');
  }

  goToNoiseMonitoring() {
    this.navCtrl.navigateForward('noise-monitoring');
  }

  goToInsurance() {
    this.navCtrl.navigateForward('insurance');
  }

  async goToGuidebookPage() {
    await this.storage.save('dialog-right-side-open', false);
    this.navCtrl.navigateForward('guidebook');
  }

  goToContactsTab(tab: string) {
    localStorage.setItem('cameFromPage', 'more');
    this.navCtrl.navigateForward(`contacts?tab=${tab}`);
  }

  goToAgents() {
    localStorage.setItem('cameFromPage', 'more');
    this.navCtrl.navigateForward('agent');
  }

  goToGoLive() {
    this.openRightSidePanel('go-live', GoLivePage, {});

  }
  
  async calculateOnboardingPercentage() {
    const onboardingGoals = await this.storage.retrieve('onboardingGoals');
    if (!onboardingGoals || onboardingGoals.length === 0) {
      this.onboardingPercentage = null;
      return;
    }
    let totalSteps = 0;
    let completedSteps = 0;
    onboardingGoals.forEach(goal => {
      totalSteps += goal.user_goal_steps?.length || 0;
      completedSteps += goal.completedSteps || 0;
    });
    if (totalSteps === 0) {
      this.onboardingPercentage = null;
      return;
    }
    this.onboardingPercentage = Math.round((completedSteps / totalSteps) * 100);
  }

  // Mirrors the desktop floating pill (app.component.refreshLivePill) so the mobile More
  // page shows the same onboarding status. Sources from the same data as the
  // onboarding-status page, so the indicator and the page never diverge.
  async refreshLivePill(): Promise<void> {
    if (!this.demoAccount.isConvertedRentalAccount()) {
      this.showLivePill = false;
      return;
    }
    try {
      const [assigned, callScheduled, callCompleted, manager, subscribeCompleted] = await Promise.all([
        this.demoAccount.getAssignedActionItemsByAddress(),
        this.demoAccount.isSetupCallScheduled(),
        this.demoAccount.isSetupCallCompleted(),
        this.accountManagerService.getDisplay(),
        this.demoAccount.isSubscribeStepCompleted(),
      ]);
      this.livePropertiesCount = assigned.filter((a) => a.isLive).length;
      this.livePropertiesTotal = assigned.length;
      this.callScheduled = callScheduled;
      this.setupCallCompleted = callCompleted;
      this.managerName = manager.name;
      this.managerPhoto = manager.photoUrl;
      let outstanding = assigned.reduce((sum, a) => sum + a.items.filter((i) => !i.done).length, 0);
      // No property yet: the placeholder's Tell Us About Your Listings + Subscribe (until subscribed).
      outstanding += assigned.length === 0
        ? 1 + (subscribeCompleted ? 0 : 1)
        : assigned.filter((a) => !a.items.some((i) => i.stepId === 'listings')).length
          + assigned.filter((a) => !a.items.some((i) => i.stepId === 'subscribe')).length;
      this.actionItemsCount = outstanding;
      this.showLivePill = true;
    } catch {
      this.showLivePill = false;
    }
  }

  // Same destination logic as the desktop pill: the TIDY AI Property Manager page once the
  // setup call is scheduled, otherwise the schedule-call flow. Refreshes on close.
  openLivePill = () => {
    // navigateTo → right-side panel on desktop, full-page route on mobile (with the
    // component's own tidy-top-nav). Registered routes: 'get-started-status' / 'get-started'.
    if (this.callScheduled || this.setupCallCompleted) {
      this.rightSidePanelService.setDialogPageTitle('My Account');
      this.rightSidePanelService.navigateTo('get-started-status', {}, OnboardingStatusComponent);
    } else {
      this.rightSidePanelService.setDialogPageTitle('Schedule Setup Call');
      this.rightSidePanelService.navigateTo('get-started', { startStep: 2 }, GetStartedFlowComponent);
    }
    this.rightSidePanelService.panelClosed().subscribe(() => this.refreshLivePill());
  };

  async goToGettingStarted() {
    await this.storage.save('getting-started-back-url', 'more');
    this.navCtrl.navigateForward('getting-started');
  }

  async goToOnboarding() {
    await this.rightSidePanelService.closeRightSidePanel();
    this.onboardingProvider.flow = 'goals';
    await this.onboardingProvider.refreshOnboardingContent();
    this.rightSidePanelService.setDialogPageTitle('Getting Started');
    this.openRightSidePanel('getting-started', OnboardingComponent, {
      title: this.onboardingProvider.title,
      subTitle: this.onboardingProvider.subTitle
    });
  }

  openGlobalSearch() {
    this.events.publish('global-search:open');
  }

}
